import { ExternalLink } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import type { InvoiceHeader, OrderDetail, OrderLine, Shipment } from '@/lib/api-types'
import { formatDate, formatMoney, formatQty } from '@/lib/format'
import { paymentChipClasses, stageChipClasses } from '@/lib/status'
import { trackingUrl } from '@/lib/tracking'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'
import { lineFulfillmentFor } from './fulfillment'
import { orderNumber } from './order-number'

export function OrderDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['order', id],
    queryFn: ({ signal }) => api.getOrder(id, { signal }),
    staleTime: 15_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this order…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="micro-label">{missing ? 'Not found' : 'Something went wrong'}</p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This order doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this order.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/orders">Back to orders</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <OrderDetailView detail={data} />
}

function OrderDetailView({ detail }: { detail: OrderDetail }) {
  // Per-line fulfillment hydrates separately (Apex round trip on the API
  // side) so the page paints immediately from the fast SOQL reads.
  const fulfillmentQuery = useQuery({
    queryKey: ['order-fulfillment', detail.id],
    queryFn: ({ signal }) => api.getOrderFulfillment(detail.id, { signal }),
    staleTime: 30_000,
    retry: false,
  })

  const facts: DescriptionItem[] = []
  if (detail.poNumber) facts.push({ label: 'PO number', value: detail.poNumber, mono: true })
  facts.push({
    label: 'Total',
    value: formatMoney(detail.amount, detail.currency),
    mono: true,
  })
  if (detail.requiredDate) {
    facts.push({
      label: 'Required',
      value: <time dateTime={detail.requiredDate}>{formatDate(detail.requiredDate)}</time>,
      mono: true,
    })
  }
  if (detail.dueDate) {
    facts.push({
      label: 'Due',
      value: <time dateTime={detail.dueDate}>{formatDate(detail.dueDate)}</time>,
      mono: true,
    })
  }
  facts.push({
    label: 'Placed',
    value: <time dateTime={detail.createdAt}>{formatDate(detail.createdAt)}</time>,
    mono: true,
  })

  return (
    <article className="mx-auto max-w-data">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/orders"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Orders
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          {orderNumber(detail)}
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title={`Order ${orderNumber(detail)}`}
        meta={
          <>
            <StatusChip label={detail.stageLabel} chipClasses={stageChipClasses(detail.stage)} />
            {detail.syncPending ? (
              <span className="font-mono text-[0.6875rem] uppercase tracking-eyebrow">
                Processing — order number to follow
              </span>
            ) : null}
          </>
        }
      />

      <DescriptionList className="mt-5 border-y border-rule/60 py-4" columns={3} items={facts} />

      {/* Lines */}
      <section aria-labelledby="order-lines-heading" className="mt-10">
        <SectionHeader id="order-lines-heading" title="Lines" />
        <div className="mt-4">
          <LinesTable detail={detail} fulfillment={fulfillmentQuery.data} />
        </div>
      </section>

      {/* Shipments */}
      <section aria-labelledby="order-shipments-heading" className="mt-10">
        <SectionHeader id="order-shipments-heading" title="Shipments" />
        {detail.shipments.length === 0 ? (
          <p className="mt-4 text-sm text-mute">Nothing has shipped yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {detail.shipments.map((s) => (
              <li key={s.id}>
                <ShipmentCard shipment={s} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Invoices */}
      <section aria-labelledby="order-invoices-heading" className="mt-10">
        <SectionHeader id="order-invoices-heading" title="Invoices" />
        {detail.invoices.length === 0 ? (
          <p className="mt-4 text-sm text-mute">No invoices on this order yet.</p>
        ) : (
          <div className="mt-4">
            <DataTable<InvoiceHeader>
              columns={INVOICE_COLUMNS}
              rows={detail.invoices}
              rowKey={(inv) => inv.id}
              caption="Invoices on this order"
            />
          </div>
        )}
      </section>
    </article>
  )
}

const INVOICE_COLUMNS: DataTableColumn<InvoiceHeader>[] = [
  {
    id: 'number',
    header: 'Invoice #',
    widthClassName: 'w-28',
    cell: (inv) => (
      <Link
        to={`/invoices/${inv.id}`}
        className="rounded-sm font-mono text-[0.8125rem] text-inkMid underline-offset-4 hover:text-brand hover:underline"
      >
        {inv.docNum ?? '—'}
      </Link>
    ),
  },
  {
    id: 'date',
    header: 'Date',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => <span className="text-sm text-inkMid">{formatDate(inv.docDate)}</span>,
  },
  {
    // No free-text field on an invoice row — status takes the slack.
    id: 'status',
    header: 'Status',
    flex: true,
    widthClassName: 'min-w-36',
    cell: (inv) => (
      <StatusChip label={inv.paymentStatusLabel} chipClasses={paymentChipClasses(inv.paymentGroup)} />
    ),
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => formatMoney(inv.docTotal, inv.currency),
  },
  {
    id: 'balance',
    header: 'Balance',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => formatMoney(inv.balanceDue, inv.currency),
  },
]

function LinesTable({
  detail,
  fulfillment,
}: {
  detail: OrderDetail
  fulfillment: Record<string, Record<string, unknown>> | undefined
}) {
  const columns: DataTableColumn<OrderLine>[] = [
    {
      id: 'product',
      header: 'Product',
      flex: true,
      widthClassName: 'min-w-48',
      cell: (line) => (
        <span className="flex flex-col">
          <span className="truncate text-[0.9375rem] font-medium text-ink" title={line.productName}>
            {line.productName}
          </span>
          <span className="mt-0.5 truncate font-mono text-xs text-mute">
            {line.itemCode ?? ''}
            {line.packSizeKg
              ? `${line.itemCode ? ' · ' : ''}${formatQty(line.packSizeKg)} kg pack`
              : ''}
          </span>
        </span>
      ),
    },
    {
      id: 'quantity',
      header: 'Quantity',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) => (
        <span>
          {formatQty(line.quantity)}
          {line.uom ? <span className="text-mute"> {line.uom}</span> : null}
        </span>
      ),
    },
    {
      id: 'pricePerKg',
      header: 'Price / kg',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) =>
        line.pricePerKg != null ? formatMoney(line.pricePerKg, detail.currency) : '—',
    },
    {
      id: 'total',
      header: 'Total',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) => formatMoney(line.lineTotal, detail.currency),
    },
    {
      id: 'shipped',
      header: 'Shipped',
      align: 'right',
      widthClassName: 'w-24',
      cell: (line) => (
        <span className="flex flex-col whitespace-nowrap">
          <span>{line.quantityShipped != null ? formatQty(line.quantityShipped) : '—'}</span>
          {line.quantityOutstanding != null && line.quantityOutstanding > 0 ? (
            <span className="text-[0.6875rem] text-mute">
              {formatQty(line.quantityOutstanding)} open
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'fulfillment',
      header: 'Fulfillment',
      widthClassName: 'w-44',
      cell: (line) => {
        const state = lineFulfillmentFor(fulfillment, line.id)
        return state ? (
          <span className="flex flex-col gap-1">
            <StatusChip label={state.label} chipClasses={state.chipClasses} />
            {state.detail ? <span className="text-xs text-mute">{state.detail}</span> : null}
          </span>
        ) : (
          <span className="font-mono text-xs text-mute">—</span>
        )
      },
    },
  ]

  return (
    <DataTable<OrderLine>
      columns={columns}
      rows={detail.lines}
      rowKey={(line) => line.id}
      caption="Order lines"
    />
  )
}

function ShipmentCard({ shipment }: { shipment: Shipment }) {
  const url = trackingUrl(shipment.carrier, shipment.trackingNumber)
  return (
    <Card padding="sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-[0.8125rem] text-inkMid">
          Delivery {shipment.docNum ?? '—'}
        </span>
        <span className="text-xs text-mute">
          {shipment.shippedAt ? `Shipped ${formatDate(shipment.shippedAt)}` : 'Ship date pending'}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-inkMid">
        {shipment.status ? <span>{shipment.status}</span> : null}
        {shipment.carrier ? <span>{shipment.carrier}</span> : null}
        {shipment.trackingNumber ? (
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-sm font-mono text-[0.8125rem] font-semibold text-brand underline-offset-4 hover:underline"
            >
              {shipment.trackingNumber}
              <ExternalLink aria-hidden="true" className="size-3.5" />
              <span className="sr-only">(opens carrier tracking)</span>
            </a>
          ) : (
            <span className="font-mono text-[0.8125rem]">{shipment.trackingNumber}</span>
          )
        ) : null}
      </div>
    </Card>
  )
}
